import { createContext, useContext, useMemo, createElement, ReactNode } from 'react';
import en from './locales/en.json';
import tr from './locales/tr.json';

type Locale = 'en' | 'tr';

const translations: Record<Locale, Record<string, any>> = { en, tr };

function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? `{{${key}}}`));
}

interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  tArray: (key: string) => string[];
  lang: Locale;
}

const I18nContext = createContext<I18nContextType>({
  t: (key: string) => key,
  tArray: () => [],
  lang: 'tr',
});

/**
 * The UI language, and therefore the language every reply comes back in — the
 * backend locks the response to whatever this resolves to.
 *
 * Strict `lang === 'tr'` used to send `tr-TR` and `TR` down the English branch,
 * and a missing param defaulted to English, so a Turkish user could be answered
 * in English. Mirror of `backend/src/utils/language.ts`: no usable value → 'tr',
 * otherwise 'tr' only when the code starts with "tr". Keep the two in sync.
 */
function detectLanguage(): Locale {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang')?.trim().toLowerCase();
  if (!lang) return 'tr';
  return lang.startsWith('tr') ? 'tr' : 'en';
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const lang = useMemo(detectLanguage, []);

  const value = useMemo<I18nContextType>(() => {
    const locale = translations[lang] || translations.en;
    const fallback = translations.en;

    return {
      lang,
      t: (key: string, params?: Record<string, string | number>) => {
        const val = getNestedValue(locale, key) ?? getNestedValue(fallback, key);
        if (typeof val !== 'string') return key;
        return params ? interpolate(val, params) : val;
      },
      tArray: (key: string) => {
        const val = getNestedValue(locale, key) ?? getNestedValue(fallback, key);
        return Array.isArray(val) ? val : [];
      },
    };
  }, [lang]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useTranslation() {
  return useContext(I18nContext);
}
